import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Box, Typography } from '@mui/material';
import type { FilterBarFilterOption } from '../contracts';

interface FilterOptionListboxProps {
  label: string;
  options: FilterBarFilterOption[];
  selectedValues: string[];
  multiple?: boolean;
  onSelect: (value: string) => void;
}

const FilterOptionListbox = ({
  label,
  options,
  selectedValues,
  multiple = false,
  onSelect,
}: FilterOptionListboxProps) => {
  const id = useId();
  const listboxRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    listboxRef.current?.focus();
  }, []);

  useEffect(() => {
    setActiveIndex(current => (current < options.length ? current : -1));
  }, [options]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!options.length) return;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex(current => {
          const direction = event.key === 'ArrowDown' ? 1 : -1;
          if (current < 0) return direction === 1 ? 0 : options.length - 1;
          const nextIndex = current + direction;
          return (nextIndex + options.length) % options.length;
        });
        break;
      case 'Home':
      case 'End':
        event.preventDefault();
        setActiveIndex(event.key === 'Home' ? 0 : options.length - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (options[activeIndex]) onSelect(options[activeIndex].value);
        break;
      default:
        if (event.code === 'Space') {
          event.preventDefault();
          if (options[activeIndex]) onSelect(options[activeIndex].value);
        }
    }
  };

  return (
    <Box
      ref={listboxRef}
      role="listbox"
      aria-label={label}
      aria-multiselectable={multiple || undefined}
      aria-activedescendant={activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      sx={{
        maxHeight: 440,
        overflowY: 'auto',
        py: 1,
        outline: 'none',
      }}
    >
      {options.map((option, index) => {
        const selected = selectedValues.includes(option.value);
        const active = index === activeIndex;

        return (
          <Box
            key={option.value}
            id={`${id}-option-${index}`}
            role="option"
            aria-selected={selected}
            onMouseMove={() => setActiveIndex(-1)}
            onClick={() => onSelect(option.value)}
            sx={{
              minHeight: multiple ? 62 : 48,
              px: multiple ? 3 : 2.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              backgroundColor: active ? 'action.hover' : selected ? 'action.selected' : 'transparent',
              outline: 'none',
              '&:hover': { backgroundColor: 'action.hover' },
            }}
          >
            {multiple &&
              (selected ? (
                <CheckBoxIcon aria-hidden="true" color="primary" />
              ) : (
                <CheckBoxOutlineBlankIcon aria-hidden="true" color="action" />
              ))}
            <Typography>{option.label}</Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default FilterOptionListbox;
